import _inheritsLoose from "@babel/runtime/helpers/inheritsLoose";
import React, { Component } from "react";
import { bool, func } from "prop-types";
import { connect, batch } from "react-redux";
import moment from "moment";
import { APP_IDS, DATE_FORMAT_ISO8601, DATE_FORMAT_PRODUCT, DATE_FORMAT_UI } from "../../constants";
import InputDate from "../../shared/components/InputDate";
import { formFieldPropTypes, pacDataPropTypes } from "../../shared/propTypes";
import { externalModelUpdate, externalRunBinds, triggerEvent } from "@icg360/rex"; // These get used on <Preview /> so we'll export them here

export var NAME = "effectiveDate"; // should match the `formField` key in the parent state

export var LABEL = "Effective Date of Proposed Changes";
export var TERM = "EffectiveDateOfProposedChanges";

var EffectiveDate = /*#__PURE__*/function (_Component) {
  _inheritsLoose(EffectiveDate, _Component);

  function EffectiveDate(props) {
    var _this;

    _this = _Component.call(this, props) || this; // Save our min/max dates in ISO_8601 to make it easier to work with
    // in conjuction with the format of `effectiveDate.value` (also ISO_8601)

    _this.handleOnChange = function (d) {
      var onChange = _this.props.onChange;
      var _this$state = _this.state,
          maxDate = _this$state.maxDate,
          minDate = _this$state.minDate;
      var selectedDateMoment = d ? moment(d) : "";
      var isValid = false;

      if (selectedDateMoment && selectedDateMoment.isValid() && selectedDateMoment.isBetween(minDate, maxDate, "day", "[]")) {
        isValid = true;
      } // Update the field's value in state


      onChange(selectedDateMoment, isValid);
    };

    var _props$pacData = props.pacData,
        applicationId = _props$pacData.applicationId,
        _props$pacData$policy = _props$pacData.policyData,
        CarrierID = _props$pacData$policy.CarrierID,
        CancellationDate = _props$pacData$policy.CancellationDate,
        _EffectiveDate = _props$pacData$policy.EffectiveDate,
        ExpirationDate = _props$pacData$policy.ExpirationDate,
        StartDate = _props$pacData$policy.StartDate,
        policyActionId = _props$pacData.policyActionId,
        _onChange = props.onChange; // We'll initialize a date as a MM/DD/YYYY representation of today
    // instead of a datetime `moment()`. This ensures that we get the same time
    // for both today's date and the minimum date, which makes our date
    // comparison more accurate.

    var todaysDate = moment(moment().format(DATE_FORMAT_PRODUCT), DATE_FORMAT_PRODUCT);

    var _minDate = moment(StartDate, DATE_FORMAT_PRODUCT);

    var _maxDate = moment(ExpirationDate, DATE_FORMAT_PRODUCT);

    var dayPickerProps = {}; // If we're in Agent Portal and this is not a particular form and the default min date is before today's date,
    // set the min date to today as we don't want to allow users to backdate
    // policy endorsements.

    if (applicationId === APP_IDS.AGENT_PORTAL && policyActionId !== "insuredcancel" && _minDate.isBefore(todaysDate)) {
      _minDate = todaysDate;
    } // If we're in Agent Portal and for the reinstallment form,
    // then we want an effective date min: 30 days prior to today max: today


    if (applicationId === APP_IDS.AGENT_PORTAL && policyActionId === "agentreinstatement") {
      var effDateProposedChanges = moment(CancellationDate);

      _onChange(effDateProposedChanges, effDateProposedChanges.isValid());
    } // If we're in Agent Portal and this is a particular form, then we
    // need some special handling. Namely, we want to only display the
    // date ranges available in the day picker.


    if (applicationId === APP_IDS.AGENT_PORTAL && policyActionId === "insuredcancel") {
      var minDateLimit = moment(todaysDate.clone()).subtract(30, "days");
      _minDate = moment(_EffectiveDate).isBefore(minDateLimit) ? moment(minDateLimit, DATE_FORMAT_PRODUCT) : moment(_EffectiveDate, DATE_FORMAT_PRODUCT);
      dayPickerProps = {
        disabledDays: {
          before: moment(_minDate, DATE_FORMAT_ISO8601).toDate(),
          after: moment(_maxDate, DATE_FORMAT_ISO8601).toDate()
        }
      };
    } // If we're in IPC Manager and it is a FedNat policy
    // and has a policy EffectiveDate prior to 07/01/2022
    // we set the minDate to 07/01/2022


    if (applicationId === APP_IDS.IPC_MANAGER && CarrierID === "FNIC") {
      var fedNatDateLimit = moment("07/01/2022", DATE_FORMAT_PRODUCT);

      if (moment(_EffectiveDate).isBefore(fedNatDateLimit)) {
        _minDate = fedNatDateLimit;
      }
    }

    _this.state = {
      minDate: _minDate.format(DATE_FORMAT_ISO8601),
      maxDate: _maxDate.format(DATE_FORMAT_ISO8601),
      dayPickerProps: dayPickerProps
    };
    return _this;
  }
  /**
   * onChange handler for date selection
   * @param {Date} d
   */


  var _proto = EffectiveDate.prototype;

  _proto.render = function render() {
    var _this$props = this.props,
        _this$props$pacData = _this$props.pacData,
        applicationId = _this$props$pacData.applicationId,
        policyActionId = _this$props$pacData.policyActionId,
        CancellationDate = _this$props$pacData.policyData.CancellationDate,
        showEffectiveDateButtons = _this$props$pacData.showEffectiveDateButtons,
        formSubmitAttempted = _this$props.formSubmitAttempted,
        _this$props$effective = _this$props.effectiveDate,
        isTouched = _this$props$effective.isTouched,
        isValid = _this$props$effective.isValid,
        value = _this$props$effective.value;
    var _this$state2 = this.state,
        maxDate = _this$state2.maxDate,
        minDate = _this$state2.minDate,
        dayPickerProps = _this$state2.dayPickerProps;
    var showError = formSubmitAttempted ? // If a submit was attempted, show an error if this field isn't valid
    !isValid : // If a submit hasn't been attempted yet, only show an error if the field
    // has been touched, it has a value, and that value is invalid
    Boolean(isTouched && value && !isValid); // Technically we don't have to do this, as these two date formats are the
    // same (see `constants.js`) but that could change some day.

    var minDateDisplay = moment(minDate, DATE_FORMAT_ISO8601).format(DATE_FORMAT_UI);
    var maxDateDisplay = moment(maxDate, DATE_FORMAT_ISO8601).format(DATE_FORMAT_UI);
    var formGroupClasses = ["form-group", showError ? "has-error" : ""].join(" ");
    var inputDateValue = APP_IDS.AGENT_PORTAL && policyActionId === "agentreinstatement" ? CancellationDate : value;
    var isReadOnly = APP_IDS.AGENT_PORTAL && policyActionId === "agentreinstatement";
    return /*#__PURE__*/React.createElement("div", {
      className: formGroupClasses,
      style: {
        marginBottom: "20px"
      }
    }, /*#__PURE__*/React.createElement("div", {
      className: "col-xs-5"
    }, /*#__PURE__*/React.createElement("label", {
      htmlFor: NAME,
      className: "control-label"
    }, LABEL)), /*#__PURE__*/React.createElement("div", {
      className: "col-xs-7"
    }, /*#__PURE__*/React.createElement(InputDate, {
      id: NAME,
      value: inputDateValue,
      onChange: this.handleOnChange,
      dayPickerProps: dayPickerProps,
      isReadOnly: isReadOnly,
      showEffectiveDateButtons: showEffectiveDateButtons
    }), showError && /*#__PURE__*/React.createElement("p", {
      className: "help-block"
    }, "The selected date will put the policy changes out of sequence."), /*#__PURE__*/React.createElement("p", {
      className: "help-block"
    }, policyActionId !== "insuredcancel" && policyActionId !== "agentreinstatement" && "Select a date between " + minDateDisplay + " and " + maxDateDisplay + ".", applicationId === APP_IDS.AGENT_PORTAL && policyActionId !== "insuredcancel" && policyActionId !== "agentreinstatement" && " To process a request outside of that date range please contact Policy Services.", policyActionId === "agentreinstatement" && "If eligible, the policy will be reinstated back to the cancellation effective date.")));
  };

  return EffectiveDate;
}(Component);

EffectiveDate.propTypes = {
  pacData: pacDataPropTypes.isRequired,
  effectiveDate: formFieldPropTypes.isRequired,
  formSubmitAttempted: bool.isRequired,
  onChange: func.isRequired
};

var mapDispatch = function mapDispatch(dispatch, _ref) {
  var pacData = _ref.pacData,
      updateFormField = _ref.updateFormField;
  return {
    onChange: function onChange(momentDate, isValid) {
      updateFormField(NAME, {
        value: momentDate && momentDate.isValid() ? momentDate.format(DATE_FORMAT_ISO8601) : "",
        isValid: isValid,
        isTouched: true
      });

      if (isValid) {
        batch(function () {
          var _data;

          dispatch(externalModelUpdate({
            formId: pacData.pacId,

            /* in the future we may want to limit this to just the child form
            formIndex: pacData.policyActionId, */
            data: (_data = {}, _data[TERM] = momentDate.format(DATE_FORMAT_PRODUCT), _data)
          }));
          dispatch(triggerEvent({
            eventName: "effective-date-of-proposed-changes-update"
          }));
          dispatch(externalRunBinds());
        });
      }
    }
  };
};

export default connect(null, mapDispatch)(EffectiveDate);